import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Popconfirm, Space } from "antd";
import {
  appTitle,
  adnPageSize,
  dateFormat,
  CriticalSituationStatus,
  getCriticalSituationOptions,
  Departement,
} from "../common/GeneralConfigurations";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import CriticalSituationListForm from "./CriticalSituationListForm";
import {
  getCriticalSituations,
  closeCriticalSituation,
} from "../../api/CriticalSituationApi";
import { EditOutlined, CloseOutlined } from "@ant-design/icons";
import { AppRoutes } from "../../AppRoutes";
import { getEmployeeByName } from "../../api/EmployeeApi";
import { userHasRolePermission } from "../../api/ApiUtils";
import { GetUserByDepartmentId } from "../../api/UserApi";
import { Link } from "react-router-dom";

const ManageCriticalSituationListPage = () => {
  const { t } = useTranslation();
  const title = t("criticalSituation.title");

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusId, setStatusId] = useState(0);
  const [employeeId, setEmployeeId] = useState(undefined);
  const [managerId, setManagerId] = useState(undefined);
  const [statusOptions, setStatusOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [criticalSituations, setCriticalSituations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState();
  const [totalRecords, setTotalRecords] = useState(0);
  const [orderBy, setOrderBy] = useState("");
  const [employeesList, setEmployeesList] = useState([]);

  const FirstLastName = (name) => {
    let fullName = name.split(" ");
    let firstName = fullName[0];
    return firstName.concat(" " + fullName[fullName.length - 1]);
  };

  useEffect(async () => {
    document.title = `${appTitle} - ${title}`;

    if (userHasRolePermission("Manager")) {
      navigate(AppRoutes.forbidden.path);
    }

    if (searchParams.get("employeeId") != undefined) {
      setEmployeeId(searchParams.get("employeeId"));
    }
    setCurrentPage(1);

    setStatusOptions(getCriticalSituationOptions());
    fetchManagers();
  }, []);

  useEffect(() => {
    if (currentPage > 0) {
      loadCriticalSituations();
    }
  }, [orderBy, currentPage, employeeId, statusId, managerId]);

  const columns = [
    {
      title: t("criticalSituation.columns.name"),
      dataIndex: "employee",
      key: "name",
      width: "20%",
      sorter: true,
      render: (text, record) => (
        <div className="AvatarName">
          {/* <Avatar size="large" src={record.image} /> */}
          <Link
            to={AppRoutes.consultantById.createPath(record.employeeId)}
            target="_blank"
          >
            {FirstLastName(text)}
          </Link>
        </div>
      ),
    },
    {
      title: t("criticalSituation.columns.description"),
      dataIndex: "description",
      key: "description",
      width: "40%",
    },
    {
      title: t("criticalSituation.columns.manager"),
      dataIndex: "manager",
      key: "manager",
      width: "15%",
    },
    {
      title: t("criticalSituation.columns.status"),
      dataIndex: "status",
      key: "status",
      width: "10%",
    },
    {
      title: t("criticalSituation.columns.reportDate"),
      dataIndex: "reportDate",
      key: "reportDate",
      width: "10%",
      sorter: true,
      render: (text) => (text ? moment(text).format(dateFormat) : undefined),
    },
    {
      title: t("criticalSituation.columns.followupDate"),
      dataIndex: "followupDate",
      key: "followupDate",
      width: "10%",
      sorter: true,
      render: (text) => (text ? moment(text).format(dateFormat) : undefined),
    },
    {
      title: t("criticalSituation.columns.action"),
      key: "action",
      width: "5%",
      render: (record) => (
        <Space style={{ display: "flex", justifyContent: "center" }}>
          <EditOutlined
            onClick={() => handleEditCriticalSituation(record.id)}
          />
          {record.statusId == CriticalSituationStatus.FollowingUp && (
            <Popconfirm
              okText={t("criticalSituation.buttons.yes")}
              cancelText={t("criticalSituation.buttons.no")}
              okButtonProps={{
                style: { backgroundColor: "red", borderColor: "red" },
              }}
              title={t("criticalSituation.messages.closeConfirmation")}
              onConfirm={() => handleCloseCriticalSituation(record.id)}
            >
              <CloseOutlined style={{ color: "red" }} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const fetchManagers = async () => {
    await GetUserByDepartmentId(Departement.Manager)
      .then((res) => {
        if (res.status === 200) {
          setManagerOptions(res.data.result.filter((u) => !u.deleted));
        } else {
          AdnMessageError(t("criticalSituation.messages.couldNotGetManagers"));
        }
      })
      .catch(() => {
        AdnMessageError(t("criticalSituation.messages.couldNotGetManagers"));
      });
  };

  const managerHandleChange = async (managerId) => {
    setManagerId(managerId);
  };

  const handleChangePage = (page) => {
    setTimeout(() => {
      setCurrentPage(page);
    }, 100);
  };

  const loadCriticalSituations = () => {
    getCriticalSituations({
      pageNumber: currentPage,
      pageSize: adnPageSize,
      orderBy,
      employeeId,
      managerId,
      statusId,
    })
      .then((res) => {
        if (res.status === 200) {
          setCriticalSituations(res.data.result);
          setTotalRecords(res.data.totalRecords);
        } else if (res && res.data && res.data.errors) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
          });
          setCriticalSituations([]);
        } else {
          AdnMessageError(
            t("criticalSituation.messages.couldNotGetCriticalSituations")
          );
        }
      })
      .catch(() =>
        AdnMessageError(
          t("criticalSituation.messages.couldNotGetCriticalSituations")
        )
      )
      .finally(() => setLoading(false));
  };

  const handleTableOrderByChanges = async (pagination, filters, sorter) => {
    setCurrentPage(1);
    sorter.order === "descend"
      ? setOrderBy("-" + sorter.field)
      : setOrderBy(sorter.field);
  };

  const statusHandleChange = (value) => {
    setCurrentPage(1);
    if (value !== undefined) {
      setStatusId(value);
    } else {
      setStatusId(undefined);
    }
  };

  const handleAddCriticalSituation = () => {
    navigate(AppRoutes.criticalSituation.path);
  };

  const handleEditCriticalSituation = (id) => {
    navigate(AppRoutes.criticalSituationById.createPath(id));
  };

  const handleCloseCriticalSituation = async (id) => {
    setLoading(true);
    await closeCriticalSituation(id)
      .then((res) => {
        if (res.status === 200) {
          loadCriticalSituations();
          AdnMessageSuccess(
            t("criticalSituation.messages.criticalSituationClosed")
          );
          return;
        } else if (res.status === 400) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
          });
        } else {
          AdnMessageError(
            t("criticalSituation.messages.couldNotCloseCriticalSituation")
          );
        }
      })
      .catch(() => {
        AdnMessageError(
          t("criticalSituation.messages.couldNotCloseCriticalSituation")
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSelectEmployee = (employee, option) => {
    setCurrentPage(1);
    setEmployeeId(option.key);
  };
  const handleClearEmployee = () => {
    setEmployeeId(undefined);
  };

  const handleSearchEmployee = async (name) => {
    name.length >= 3 ? await fetchEmployees(name) : setEmployeesList([]);
  };

  const fetchEmployees = async (name) => {
    await getEmployeeByName(name)
      .then((res) => {
        if (res.status === 200) {
          setEmployeesList(res.data);
        } else {
          AdnMessageError(
            t("criticalSituation.messages.couldNotGetConsultants")
          );
        }
      })
      .catch((e) => {
        AdnMessageError(t("criticalSituation.messages.couldNotGetConsultants"));
      });
  };

  return (
    <CriticalSituationListForm
      title={title}
      loading={loading}
      columns={columns}
      statusOptions={statusOptions}
      statusHandleChange={statusHandleChange}
      criticalSituations={criticalSituations}
      currentPage={currentPage}
      totalRecords={totalRecords}
      pageSize={adnPageSize}
      handleTableOrderByChanges={handleTableOrderByChanges}
      handleChangePage={handleChangePage}
      handleAddCriticalSituation={handleAddCriticalSituation}
      handleSearchEmployee={handleSearchEmployee}
      handleSelectEmployee={handleSelectEmployee}
      handleClearEmployee={handleClearEmployee}
      employeesList={employeesList}
      managerOptions={managerOptions}
      managerHandleChange={managerHandleChange}
    />
  );
};

export default ManageCriticalSituationListPage;
